import { z } from "zod";

// TES-1237 S3, TES-1367 — the patient's rights over her structured
// Patientminne, exercised through Ella's staff.
//
// Emelie, Ö-462 (TES-1362, 2026-10-15): Patientminnet is an internal,
// permission-controlled coach tool. There is no patient self-service view.
// "Om patienten begär tillgång ska Ella kunna lämna ut innehållet begripligt
// och hantera rättelse, borttagning eller begränsning genom en dokumenterad
// manuell process." A request arrives by mail, chat or phone; the coach
// registers it here on the patient's behalf, and resolves it. Access itself
// is the Art. 15 export (TES-1324) and the manual process in TES-1368.

export const PATIENT_MEMORY_REQUEST_KINDS = [
  "correction",
  "deletion",
  "restriction",
] as const;
export const patientMemoryRequestKindSchema = z.enum(
  PATIENT_MEMORY_REQUEST_KINDS,
);
export type PatientMemoryRequestKind = z.infer<
  typeof patientMemoryRequestKindSchema
>;

export const PATIENT_MEMORY_REQUEST_KIND_LABELS: Record<
  PatientMemoryRequestKind,
  string
> = {
  correction: "Rättelse",
  deletion: "Borttagning",
  restriction: "Begränsning",
};

export const PATIENT_MEMORY_REQUEST_STATUSES = [
  "open",
  "resolved",
  "declined",
] as const;
export const patientMemoryRequestStatusSchema = z.enum(
  PATIENT_MEMORY_REQUEST_STATUSES,
);
export type PatientMemoryRequestStatus = z.infer<
  typeof patientMemoryRequestStatusSchema
>;

// How the request reached Ella. Recorded in the audit row only.
export const PATIENT_MEMORY_REQUEST_CHANNELS = [
  "email",
  "chat",
  "phone",
  "letter",
  "other",
] as const;
export const PATIENT_MEMORY_REQUEST_CHANNEL_LABELS: Record<
  (typeof PATIENT_MEMORY_REQUEST_CHANNELS)[number],
  string
> = {
  email: "E-post",
  chat: "Chatt",
  phone: "Telefon",
  letter: "Brev",
  other: "Annat",
};

export const PATIENT_MEMORY_CORRECTION_MAX_CHARS = 1000;

// The coach registers a request the patient made outside the app.
export const registerCoachMemoryRequestSchema = z
  .object({
    itemId: z.string().uuid(),
    kind: patientMemoryRequestKindSchema,
    // Her request in the staff member's words, required for every kind.
    // Emelie, Ö-605 (2026-10-21): "Gemensamt obligatoriskt fält ”Beskriv
    // patientens begäran” passar alla typer bättre än bara ”Vad vill
    // patienten ändra?”."
    text: z.string().trim().max(PATIENT_MEMORY_CORRECTION_MAX_CHARS).optional(),
    receivedVia: z.enum(PATIENT_MEMORY_REQUEST_CHANNELS),
  })
  .refine((v) => Boolean(v.text), {
    message: "request_needs_text",
    path: ["text"],
  });
export type RegisterCoachMemoryRequest = z.infer<
  typeof registerCoachMemoryRequestSchema
>;

// What registering answers: a receipt with no memory content. The admin
// registers requests too (TES-1367, Simon 2026-10-15) and must not read an
// entry's text through this route.
export const registeredCoachMemoryRequestSchema = z.object({
  id: z.string(),
  itemId: z.string(),
  kind: patientMemoryRequestKindSchema,
  status: patientMemoryRequestStatusSchema,
  createdAt: z.string(),
});
export type RegisteredCoachMemoryRequest = z.infer<
  typeof registeredCoachMemoryRequestSchema
>;

// The coach's (or admin's) view of a request.
export const coachMemoryRequestDtoSchema = z.object({
  id: z.string(),
  itemId: z.string(),
  kind: patientMemoryRequestKindSchema,
  // Her description of the request. Null on deletion and restriction
  // requests registered before Ö-605, when only a correction carried text.
  body: z.string().nullable(),
  status: patientMemoryRequestStatusSchema,
  resolutionNote: z.string().nullable(),
  createdAt: z.string(),
  resolvedAt: z.string().nullable(),
  // The entry as it reads now ("" once erased).
  itemBody: z.string(),
  itemCategory: z.string(),
});
export type CoachMemoryRequestDto = z.infer<typeof coachMemoryRequestDtoSchema>;

//   resolved  correction: the coach has revised the entry
//   declined  any kind, with a note the patient can be told
//   erase     deletion: the entry and every earlier version are scrubbed
//   restrict  restriction: the entry is kept but no longer used (lifecycle
//             `restricted`)
export const resolveCoachMemoryRequestSchema = z
  .object({
    action: z.enum(["resolved", "declined", "erase", "restrict"]),
    note: z.string().trim().max(1000).optional(),
  })
  .refine((v) => v.action !== "declined" || Boolean(v.note), {
    message: "decline_needs_note",
    path: ["note"],
  });
export type ResolveCoachMemoryRequest = z.infer<
  typeof resolveCoachMemoryRequestSchema
>;
